import { supabase } from '../config/supabase';
import { Organization, OrganizationWithStats, OrganizationStatus } from '../types';

// Helper to convert DB row to Organization type
const toOrganization = (row: Record<string, unknown>): Organization => ({
  id: row.id as string,
  name: row.name as string,
  slug: row.slug as string | undefined,
  contactName: row.contact_name as string | undefined,
  contactEmail: row.contact_email as string | undefined,
  contactPhone: row.contact_phone as string | undefined,
  logoUrl: row.logo_url as string | undefined,
  address: row.address as string | undefined,
  notes: row.notes as string | undefined,
  status: row.status as OrganizationStatus,
  contractStart: row.contract_start ? new Date(row.contract_start as string) : undefined,
  contractEnd: row.contract_end ? new Date(row.contract_end as string) : undefined,
  createdAt: new Date(row.created_at as string),
  updatedAt: new Date(row.updated_at as string)
});

export const organizationsService = {
  // Get all organizations (super_admin only)
  async getAll(): Promise<Organization[]> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .order('name');

    if (error) {
      console.error('Error fetching organizations:', error);
      throw new Error('Impossible de charger les organisations');
    }

    return (data || []).map(toOrganization);
  },

  // Get organizations with stats (super_admin dashboard)
  async getAllWithStats(): Promise<OrganizationWithStats[]> {
    const { data: orgs, error: orgsError } = await supabase
      .from('organizations')
      .select('*')
      .order('name');

    if (orgsError) {
      console.error('Error fetching organizations:', orgsError);
      throw new Error('Impossible de charger les organisations');
    }

    // Get learner stats for each organization
    const orgsWithStats: OrganizationWithStats[] = await Promise.all(
      (orgs || []).map(async (org) => {
        const { data: stats } = await supabase
          .from('learners')
          .select('id, status')
          .eq('organization_id', org.id);

        const learnersCount = stats?.length || 0;
        const completedCount = stats?.filter(l => l.status === 'completed').length || 0;
        const completionRate = learnersCount > 0
          ? Math.round((completedCount / learnersCount) * 100)
          : 0;

        return {
          ...toOrganization(org),
          learnersCount,
          completedCount,
          completionRate
        };
      })
    );

    return orgsWithStats;
  },

  // Get single organization by ID
  async getById(id: string): Promise<Organization | null> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      console.error('Error fetching organization:', error);
      throw new Error('Impossible de charger l\'organisation');
    }

    return data ? toOrganization(data) : null;
  },

  // Get organization by slug
  async getBySlug(slug: string): Promise<Organization | null> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('slug', slug)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      console.error('Error fetching organization by slug:', error);
      throw new Error('Impossible de charger l\'organisation');
    }

    return data ? toOrganization(data) : null;
  },

  // Create new organization
  async create(organization: Omit<Organization, 'id' | 'createdAt' | 'updatedAt'>): Promise<Organization> {
    const insertData = {
      name: organization.name,
      slug: organization.slug || organization.name.toLowerCase().replace(/\s+/g, '-'),
      contact_name: organization.contactName,
      contact_email: organization.contactEmail,
      contact_phone: organization.contactPhone,
      logo_url: organization.logoUrl,
      address: organization.address,
      notes: organization.notes,
      status: organization.status || 'prospect',
      contract_start: organization.contractStart?.toISOString(),
      contract_end: organization.contractEnd?.toISOString()
    };

    const { data, error } = await supabase
      .from('organizations')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      console.error('Error creating organization:', error);
      if (error.code === '23505') {
        throw new Error('Une organisation avec ce nom existe déjà');
      }
      throw new Error('Impossible de créer l\'organisation');
    }

    return toOrganization(data);
  },

  // Update organization
  async update(id: string, updates: Partial<Omit<Organization, 'id' | 'createdAt' | 'updatedAt'>>): Promise<Organization> {
    const updateData: Record<string, unknown> = {
      updated_at: new Date().toISOString()
    };

    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.slug !== undefined) updateData.slug = updates.slug;
    if (updates.contactName !== undefined) updateData.contact_name = updates.contactName;
    if (updates.contactEmail !== undefined) updateData.contact_email = updates.contactEmail;
    if (updates.contactPhone !== undefined) updateData.contact_phone = updates.contactPhone;
    if (updates.logoUrl !== undefined) updateData.logo_url = updates.logoUrl;
    if (updates.address !== undefined) updateData.address = updates.address;
    if (updates.notes !== undefined) updateData.notes = updates.notes;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.contractStart !== undefined) updateData.contract_start = updates.contractStart?.toISOString();
    if (updates.contractEnd !== undefined) updateData.contract_end = updates.contractEnd?.toISOString();

    const { data, error } = await supabase
      .from('organizations')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating organization:', error);
      throw new Error('Impossible de mettre à jour l\'organisation');
    }

    return toOrganization(data);
  },

  // Delete organization
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('organizations')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting organization:', error);
      throw new Error('Impossible de supprimer l\'organisation');
    }
  },

  // Get organizations by status
  async getByStatus(status: OrganizationStatus): Promise<Organization[]> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('status', status)
      .order('name');

    if (error) {
      console.error('Error fetching organizations by status:', error);
      throw new Error('Impossible de charger les organisations');
    }

    return (data || []).map(toOrganization);
  },

  // Search organizations
  async search(query: string): Promise<Organization[]> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .or(`name.ilike.%${query}%,contact_name.ilike.%${query}%,contact_email.ilike.%${query}%`)
      .order('name');

    if (error) {
      console.error('Error searching organizations:', error);
      throw new Error('Erreur lors de la recherche');
    }

    return (data || []).map(toOrganization);
  },

  // Get organization stats for dashboard
  async getStats(organizationId: string): Promise<{
    learnersCount: number;
    activeLearnersCount: number;
    completedLearnersCount: number;
    completionRate: number;
  }> {
    const { data: learners, error } = await supabase
      .from('learners')
      .select('id, status')
      .eq('organization_id', organizationId);

    if (error) {
      console.error('Error fetching organization stats:', error);
      throw new Error('Impossible de charger les statistiques');
    }

    const learnersCount = learners?.length || 0;
    const activeLearnersCount = learners?.filter(l => l.status === 'active').length || 0;
    const completedLearnersCount = learners?.filter(l => l.status === 'completed').length || 0;
    const completionRate = learnersCount > 0
      ? Math.round((completedLearnersCount / learnersCount) * 100)
      : 0;

    return {
      learnersCount,
      activeLearnersCount,
      completedLearnersCount,
      completionRate
    };
  }
};
